import type { AuthSessionData } from '../../platform-types/src'
import { fetchAuthSession, logoutAuthSession } from '../../api-client/src'
import { AUTH_LOGIN_PATH, type AuthApp, DEFAULT_API_BASE } from '../../config/src'

export function currentReturnPath(location: Location = window.location): string {
  return `${location.pathname}${location.search}${location.hash}` || '/'
}

export function preferredUserLabel(profile: AuthSessionData | null): string | null {
  if (!profile) return null
  return profile.name || profile.email || profile.id || null
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function collectStrings(...values: unknown[]): string[] {
  const result: string[] = []
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      result.push(value.trim())
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          result.push(item.trim())
        } else if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>
          const label = firstString(record.name, record.display_name, record.id, record.org_id, record.team_id)
          if (label) result.push(label)
        }
      }
    }
  }
  return Array.from(new Set(result))
}

export interface AuthBoundarySummary {
  tenantLabel: string
  organizationLabel: string | null
  teamLabel: string | null
  appLabel: string
  appTenantLabel: string | null
  adminScopeLabel: 'Platform admin' | 'Contextful admin' | 'Tenant admin' | 'Organization admin' | 'Member'
}

export function authBoundarySummary(profile: AuthSessionData | null): AuthBoundarySummary {
  const claims = profile?.claims || {}
  const appIdentity = profile?.app_identity
  const tenantLabel = firstString(
    claims.tenant_name,
    claims.tenant,
    profile?.tenant_id,
    appIdentity?.tenant_id,
    claims.tenant_id,
    claims.tid,
  ) || 'Tenant unavailable'
  const organizations = collectStrings(
    claims.organization_name,
    claims.organization,
    claims.org_name,
    claims.org_id,
    claims.organizations,
    appIdentity?.organizations,
  )
  const teams = collectStrings(claims.team_name, claims.team, claims.team_id, claims.teams, profile?.teams, appIdentity?.teams)
  const roles = new Set((profile?.roles || []).map((role) => String(role)))
  const platformAdmin = roles.has('super_admin') || roles.has('superadmin') || roles.has('platform_admin')
  const contextfulAdmin = roles.has('contextful_admin') || roles.has('contextful:admin')
  const tenantAdmin = platformAdmin || contextfulAdmin || roles.has('admin') || Array.from(roles).some((role) => role.startsWith('tenant:') && role.endsWith(':admin')) || roles.has('tenant_admin')
  const organizationAdmin = roles.has('organization_admin') || roles.has('org_admin') || roles.has('iam.organizations.*')

  return {
    tenantLabel,
    organizationLabel: organizations[0] || null,
    teamLabel: teams[0] || null,
    appLabel: appIdentity?.app_display_name || profile?.app || 'Aide',
    appTenantLabel: appIdentity?.app_tenant_id || null,
    adminScopeLabel: platformAdmin ? 'Platform admin' : contextfulAdmin ? 'Contextful admin' : tenantAdmin ? 'Tenant admin' : organizationAdmin ? 'Organization admin' : 'Member',
  }
}

export function buildBackendLoginUrl(
  app: AuthApp,
  nextPath: string = currentReturnPath(),
  origin: string = window.location.origin,
  apiBase: string = DEFAULT_API_BASE,
  intent?: 'signup',
  loginHint?: string,
  tenant?: string,
): string {
  const url = new URL(`${apiBase}${AUTH_LOGIN_PATH}`, origin)
  url.searchParams.set('app', app)
  url.searchParams.set('next', nextPath || '/')
  if (intent) url.searchParams.set('intent', intent)
  if (loginHint?.trim()) url.searchParams.set('login_hint', loginHint.trim())
  if (tenant) url.searchParams.set('tenant', tenant)
  return url.toString()
}
export { fetchAuthSession, logoutAuthSession }
export type { AuthSessionData }
